import { useCallback, useEffect, useMemo, useState } from "react";
import { getSessionMessages, listAiSessions } from "../../api/sherpa/sherpaApi.js";

const createSessionRecord = () => {
  const now = new Date().toISOString();
  return {
    id: `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: "New Chat",
    createdAt: now,
    updatedAt: now,
    remoteSessionId: null,
    dbSessionId: null,
    sessionIntent: "fresh",
    resumeChatId: null,
    isArchived: false,
    readOnly: false,
    archivedSessionId: null,
    hydrated: true,
    messages: [],
  };
};

const getSessionTitle = (messages) => {
  const firstUserMessage = messages.find((message) => message.role === "user");
  if (!firstUserMessage?.content) return "New Chat";
  return firstUserMessage.content.trim().slice(0, 80) || "New Chat";
};

export const useSherpaSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [isLoadingSessionMessages, setIsLoadingSessionMessages] = useState(false);
  const [sessionsError, setSessionsError] = useState(null);

  useEffect(() => {
    localStorage.removeItem("sherpa-chat-sessions-v1");
  }, []);

  const normalizeArchivedSession = useCallback((session) => {
    const sessionId = session?.session_id ?? session?.sessionId;
    const startedAt = session?.started_at || session?.startedAt || new Date().toISOString();
    const topicTitle = Array.isArray(session?.topics) ? session.topics[0] : "";
    return {
      id: `archived-${sessionId}`,
      title: topicTitle || session?.summary || `Session #${sessionId}`,
      createdAt: startedAt,
      updatedAt: session?.ended_at || session?.endedAt || startedAt,
      remoteSessionId: null,
      dbSessionId: sessionId || null,
      messageCount: Number(session?.message_count ?? session?.messageCount ?? 0),
      summary: session?.summary || "",
      topics: Array.isArray(session?.topics) ? session.topics : [],
      sessionIntent: "resume",
      isArchived: true,
      readOnly: true,
      archivedSessionId: sessionId || null,
      hydrated: false,
      messages: [],
    };
  }, []);

  const populateArchivedSessions = useCallback((rawSessionsList) => {
    const archived = Array.isArray(rawSessionsList)
      ? rawSessionsList
          .map(normalizeArchivedSession)
          .filter((session) => session.archivedSessionId)
      : [];

    setSessions((previous) => {
      const active = previous.filter((session) => !session.isArchived);
      return [...active, ...archived].sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
    });
    setIsLoadingSessions(false);
  }, [normalizeArchivedSession]);

  const fetchArchivedSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    setSessionsError(null);
    try {
      const response = await listAiSessions();
      const rawSessions = Array.isArray(response) ? response : response?.sessions || [];
      populateArchivedSessions(rawSessions);
      return rawSessions;
    } catch (error) {
      console.warn("Failed to load archived Sherpa sessions", error);
      setSessionsError("Unable to load session history. Please try again.");
      setIsLoadingSessions(false);
      return [];
    }
  }, [populateArchivedSessions]);

  const createNewSession = useCallback(({ intent = "fresh", resumeChatId = null } = {}) => {
    const nextSession = createSessionRecord();
    nextSession.sessionIntent = intent;
    nextSession.resumeChatId = resumeChatId;
    setSessions((previous) => [nextSession, ...previous]);
    setSelectedSessionId(nextSession.id);
    return nextSession.id;
  }, []);

  const selectSession = useCallback((sessionId) => {
    setSelectedSessionId(sessionId);
  }, []);

  const loadSessionMessages = useCallback(async (sessionId) => {
    const selected = sessions.find((session) => session.id === sessionId);
    if (!selected || !selected.isArchived || selected.hydrated) return;
    if (!selected.archivedSessionId) return;

    setIsLoadingSessionMessages(true);
    try {
      const response = await getSessionMessages(selected.archivedSessionId);
      const rawMessages = Array.isArray(response) ? response : response?.messages;
      const messages = Array.isArray(rawMessages)
        ? rawMessages.map((message) => ({
            id: `archived-msg-${message.id || message.message_id || Date.now()}`,
            messageId: message.id || message.message_id || null,
            role: message.role === "assistant" || message.isCoach ? "assistant" : "user",
            content: message.content || message.text || "",
            flags: [],
            createdAt: message.created_at || message.createdAt || new Date().toISOString(),
          }))
        : [];

      setSessions((previous) =>
        previous.map((session) =>
          session.id === sessionId
            ? {
                ...session,
                messages,
                hydrated: true,
              }
            : session
        )
      );
    } catch (error) {
      console.warn("Failed to load archived session messages", error);
    } finally {
      setIsLoadingSessionMessages(false);
    }
  }, [sessions]);

  const openArchivedSession = useCallback(
    async (archivedSessionId) => {
      const sessionId = String(archivedSessionId);
      if (!archivedSessionId || sessionId === "null" || sessionId === "undefined") {
        throw new Error("This session does not have a valid session ID.");
      }
      const localId = `archived-${sessionId}`;
      let selected = sessions.find((session) => session.id === localId);
      if (selected?.hydrated) {
        setSelectedSessionId(localId);
        return selected;
      }

      setIsLoadingSessionMessages(true);
      try {
        const response = await getSessionMessages(sessionId);
        const rawMessages = Array.isArray(response) ? response : response?.messages;
        const messages = Array.isArray(rawMessages)
          ? rawMessages.map((message, index) => ({
              id: `archived-msg-${message.id || message.message_id || `${sessionId}-${index}`}`,
              messageId: message.id || message.message_id || null,
              role: message.role === "assistant" || message.isCoach ? "assistant" : "user",
              content: message.content || message.text || "",
              flags: [],
              createdAt: message.created_at || message.createdAt || new Date().toISOString(),
            }))
          : [];
        selected = selected || normalizeArchivedSession({
          session_id: sessionId,
          message_count: messages.length,
        });
        setSessions((previous) =>
          previous.some((session) => session.id === localId)
            ? previous.map((session) =>
                session.id === localId ? { ...session, messages, hydrated: true } : session
              )
            : [...previous, { ...selected, messages, hydrated: true }]
        );
        setSelectedSessionId(localId);
        return { ...selected, messages, hydrated: true };
      } finally {
        setIsLoadingSessionMessages(false);
      }
    },
    [normalizeArchivedSession, sessions]
  );

  const updateSessionMessages = useCallback((sessionId, nextMessages) => {
    setSessions((previous) =>
      previous
        .map((session) => {
          if (session.id !== sessionId) return session;
          return {
            ...session,
            messages: nextMessages,
            updatedAt: new Date().toISOString(),
            title: getSessionTitle(nextMessages),
          };
        })
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )
    );
  }, []);

  const updateSessionMeta = useCallback((sessionId, updates) => {
    setSessions((previous) =>
      previous.map((session) =>
        session.id === sessionId
          ? {
              ...session,
              ...updates,
              updatedAt: updates.updatedAt || new Date().toISOString(),
            }
          : session
      )
    );
  }, []);

  const currentSession = useMemo(
    () => sessions.find((session) => session.id === selectedSessionId) || null,
    [selectedSessionId, sessions]
  );

  return {
    sessions,
    currentSession,
    selectedSessionId,
    isLoadingSessions,
    isLoadingSessionMessages,
    sessionsError,
    createNewSession,
    selectSession,
    loadSessionMessages,
    updateSessionMessages,
    updateSessionMeta,
    populateArchivedSessions,
    fetchArchivedSessions,
    openArchivedSession,
  };
};
